// apps/web/src/routes/Dashboard.tsx
//
// /dashboard — authenticated home view. Per phase-1-5 §7, shows
// profile summary + last login location (the API provides the
// masked IP; the client never attempts to geolocate independently).
// Loading = skeleton cards; error = inline retry banner; "stale but
// visible" not applicable here (no prior fetch on first load).

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGet, ApiError } from '../lib/apiClient.js';
import type { DashboardResponse } from '../lib/api.js';
import { SkeletonBlock } from '../components/ui/Skeleton.js';
import { Alert } from '../components/ui/Alert.js';
import { Button } from '../components/ui/Button.js';

type State =
  | { kind: 'loading' }
  | { kind: 'ready'; data: DashboardResponse; requestId: string }
  | { kind: 'error'; message: string; requestId: string | null };

export function Dashboard(): JSX.Element {
  const [state, setState] = useState<State>({ kind: 'loading' });

  const load = useCallback(async (): Promise<void> => {
    setState({ kind: 'loading' });
    try {
      const res = await apiGet<DashboardResponse>('/api/v1/dashboard');
      setState({ kind: 'ready', data: res.data, requestId: res.requestId });
    } catch (err) {
      const apiErr = err as ApiError;
      setState({
        kind: 'error',
        message: apiErr?.message ?? 'Couldn\'t load your dashboard.',
        requestId: apiErr?.requestId ?? null,
      });
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  if (state.kind === 'loading') {
    return (
      <section className="mx-auto max-w-3xl px-4 py-12">
        <SkeletonBlock className="h-8 w-64" />
        <SkeletonBlock className="mt-2 h-4 w-48" />
        <div className="mt-8 grid gap-4 sm:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="rounded border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
              <SkeletonBlock className="h-3 w-20" />
              <SkeletonBlock className="mt-3 h-7 w-16" />
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (state.kind === 'error') {
    return (
      <section className="mx-auto max-w-3xl px-4 py-12">
        <Alert variant="error" title="Couldn't load your dashboard" requestId={state.requestId}>
          {state.message}
        </Alert>
        <div className="mt-4">
          <Button onClick={() => void load()}>Retry</Button>
        </div>
      </section>
    );
  }

  const { data } = state;
  const { user, stats } = data;
  const memberSince = new Date(user.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long' });

  return (
    <section className="mx-auto max-w-3xl px-4 py-12">
      <h1 className="text-2xl font-bold">Hello, {user.firstName}.</h1>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Member since {memberSince}.
      </p>

      <div className="mt-8 grid gap-4 sm:grid-cols-3">
        <Stat label="Total logins" value={String(stats.totalLogins)} />
        <Stat label="Account status" value={stats.accountStatus} />
        <Stat label="Last sign-in" value={stats.lastLoginLocation ?? '—'} />
      </div>

      <div className="mt-8 rounded border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
        <h2 className="text-sm font-medium text-gray-800 dark:text-gray-200">Your profile</h2>
        <dl className="mt-3 grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
          <dt className="text-gray-500 dark:text-gray-400">Display name</dt>
          <dd>{user.displayName}</dd>
          <dt className="text-gray-500 dark:text-gray-400">Handle</dt>
          <dd>@{user.handle}</dd>
          <dt className="text-gray-500 dark:text-gray-400">Email</dt>
          <dd>{user.email ?? '—'}{user.email && !user.emailVerified ? ' (unverified)' : null}</dd>
          <dt className="text-gray-500 dark:text-gray-400">Phone</dt>
          <dd>{user.phone ?? '—'}{user.phone && !user.phoneVerified ? ' (unverified)' : null}</dd>
        </dl>
        <div className="mt-4 flex gap-2">
          <Link to="/settings/profile" className="text-sm text-brand-600 hover:underline dark:text-brand-400">
            Edit profile
          </Link>
          <span className="text-gray-300 dark:text-gray-700">·</span>
          <Link to="/settings/security" className="text-sm text-brand-600 hover:underline dark:text-brand-400">
            Active sessions
          </Link>
        </div>
      </div>
    </section>
  );
}

function Stat({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="rounded border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
      <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</div>
      <div className="mt-2 text-2xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}
